/**
 * Manual de Revisión Final.
 * Verified behavior (V17+), derived from code — re-verified V23+:
 *  - GASTOS: somete recibos como cualquier rol; al someter su PROPIO recibo queda
 *    APROBADO automáticamente (createReceipt 488-491: reviewer → status 'approved').
 *    Es el aprobador FINAL: actúa sobre recibos en 'pending_review'. Un recibo llega
 *    a 'pending_review' SOLO cuando TODAS sus partidas están aprobadas en el Nivel 1
 *    (deriveReceiptStatus 98) — en recibos multi-supervisor la aprobación L1 es
 *    partida por partida. Antes de aprobar DEBE asignar un Proveedor Sage
 *    (reviewerApproveReceipt 1150-1154). Acciones: "Aprobar Recibo" o
 *    "Devolver a Supervisor". NO hay "Rechazar" en la revisión final.
 *    DEVOLUCIÓN respeta el origen (V23, reviewerApproveReceipt 1221-1258):
 *      · Chofer/Supervisor, NO tarjeta "Total"  → status 'pending'  → cola del Ingeniero (L1).
 *      · Ingeniero/Gerente/Revisión/Admin, o CUALQUIER tarjeta "Total" → 'sent_back' → autor.
 *  - NÓMINA: aprobador final (clerk) en 'pending_clerk' → 'approved' (approveSheet
 *    1981-1988). Devolver → al Gerente (3 niveles) o al Ingeniero (2 niveles)
 *    (returnSheet 2138-2147). La cola es POR PROYECTO: aunque cualquier reviewer puede
 *    aprobar en este nivel (isReviewer 1983), solo ve las tarjetas de los proyectos donde
 *    es el clerk asignado, resuelto vía COALESCE(nomina_reviewer_id, final_reviewer_id).
 *    Puede crear tarjetas (al someterlas quedan aprobadas, submitSheet 1798-1799).
 *  - CONTABILIDAD: tiene acceso a la exportación Sage 50 (módulo "Sage Report" /
 *    /contabilidad; ProtectedRoute 44-45, PortalLayout 49, accountingRoutes 25-27).
 * Contenido derivado de: receiptController (createReceipt, deriveReceiptStatus,
 * reviewerApproveReceipt), SupervisorDetailPage, nominaController (approveSheet,
 * returnSheet, submitSheet), TarjetaDetailPage, AccountingPortal.
 */
export default {
  roleKey: 'reviewer',
  roleLabel: 'Revisión Final',
  icon: '🔍',
  subtitle: 'Aprobación final de Gastos y Nómina, y exportación a Sage 50. También puede someter y crear tarjetas.',
  sections: [
    {
      id: 'someter-gasto',
      group: 'Gastos',
      title: 'Cómo someter un gasto',
      blocks: [
        { type: 'paragraph', text: 'Usted puede someter recibos igual que cualquier usuario.' },
        { type: 'steps', items: [
          'Abra "Gastos" → "Mis Recibos" y pulse "+ Nuevo Gasto".',
          'Suba la foto o el PDF; la IA autocompleta proveedor, fecha, subtotal y partidas. Revise que coincidan.',
          'Complete los datos del recibo y, en cada partida, el Proyecto, la Descripción y el Monto.',
          'Pulse "Enviar Reporte".',
        ] },
        { type: 'note', variant: 'warning', text: 'Como usted es el aprobador final, su PROPIO recibo queda aprobado automáticamente al someterlo. Asegúrese de que esté correcto antes de enviarlo.' },
      ],
    },
    {
      id: 'revisar-aprobar',
      group: 'Gastos',
      title: 'Revisar y aprobar recibos (aprobación final)',
      blocks: [
        { type: 'paragraph', text: 'Usted es la última línea de aprobación de Gastos. Revisa los recibos en estado "Pendiente de Revisión".' },
        { type: 'note', variant: 'info', text: 'Un recibo solo llega a "Pendiente de Revisión" cuando TODAS sus partidas ya fueron aprobadas en el Nivel 1. En los recibos con varios supervisores, esa aprobación del Ingeniero(a) ocurre partida por partida (sección por sección): el recibo no le llega para revisión final hasta que no quede ninguna partida pendiente en el Nivel 1.' },
        { type: 'steps', items: [
          'Abra "Gastos" → "Revisión Final" y seleccione un recibo.',
          'Revise todas las partidas, sus códigos (Tarea / Código GL) y los montos.',
          'Asigne el Proveedor Sage en la sección "Contabilidad Sage" (es obligatorio para poder aprobar).',
          'Pulse "Aprobar Recibo".',
        ] },
        { type: 'note', variant: 'warning', text: 'No podrá aprobar sin asignar un Proveedor Sage: aparece "Debes asignar un Proveedor Sage antes de aprobar este recibo". El total de las partidas (con impuestos) debe coincidir con el Total del Recibo (tolerancia $0.02).' },
        { type: 'note', variant: 'info', text: 'En recibos de tarjeta "Total" (que omiten el Nivel 1), usted también debe completar la codificación (Proyecto, Tarea o Código GL) antes de aprobar.' },
      ],
    },
    {
      id: 'editar-campos',
      group: 'Gastos',
      title: 'Editar campos y asignar Proveedor Sage',
      blocks: [
        { type: 'paragraph', text: 'Antes de aprobar, usted puede corregir el recibo sin tener que devolverlo.' },
        { type: 'list', items: [
          'Editar en línea descripción, monto, cantidad, unidad e impuesto de las partidas.',
          'Codificar o corregir la Tarea / Código GL y el Centro de Costo.',
          'Asignar el Proveedor Sage (obligatorio antes de aprobar) en la sección "Contabilidad Sage".',
        ] },
        { type: 'note', variant: 'info', text: 'Una vez aprobado (o pagado) el recibo, queda bloqueado: cualquier corrección posterior requiere devolverlo primero.' },
      ],
    },
    {
      id: 'devolver-recibo',
      group: 'Gastos',
      title: 'Devolver un recibo',
      blocks: [
        { type: 'paragraph', text: 'Cuando un recibo necesita correcciones, usted lo devuelve desde la revisión final. A dónde regresa el recibo depende de quién lo sometió:' },
        { type: 'list', items: [
          'Recibos sometidos por un Chofer o Supervisor (que NO sean de tarjeta "Total"): el recibo vuelve al estado "Pendiente" y regresa a la cola del Ingeniero(a) (Nivel 1) — no a quien lo subió. El Ingeniero(a) lo revisa de nuevo antes de que vuelva a usted.',
          'Recibos sometidos por un Ingeniero(a), Gerente, Revisión Final o Administrador, O CUALQUIER recibo de tarjeta "Total": el recibo queda "Devuelto" y regresa directamente a quien lo sometió, porque ese flujo no pasa por el Ingeniero(a).',
        ] },
        { type: 'steps', items: [
          'En el recibo en "Pendiente de Revisión", pulse "Devolver a Supervisor".',
          'Indique el motivo (es obligatorio) y las notas necesarias.',
          'El sistema envía el recibo al destino que corresponde según quién lo sometió (ver arriba) y notifica a esa persona.',
        ] },
        { type: 'note', variant: 'warning', text: 'El botón se llama "Devolver a Supervisor", pero el destino real depende de quién sometió el recibo: puede regresar a la cola del Ingeniero(a) (Nivel 1) o al autor original. No siempre va a un supervisor.' },
        { type: 'note', variant: 'info', text: 'En la revisión final usted aprueba o devuelve. El "Rechazo" de partidas no es parte de la revisión final: ocurre en el Nivel 1 (Ingeniero).' },
      ],
    },
    {
      id: 'revisar-pendientes',
      group: 'Nómina',
      title: 'Revisar tarjetas pendientes (validación AMGtime)',
      blocks: [
        { type: 'paragraph', text: 'En Nómina usted es el aprobador final de las tarjetas en estado "Revisión Final" (pasaron por el Ingeniero y, en proyectos de 3 niveles, también por el Gerente).' },
        { type: 'note', variant: 'info', text: 'Su cola de Nómina es POR PROYECTO: solo verá las tarjetas de los proyectos donde usted es la Revisión Final asignada. Aunque cualquier usuario con rol de Revisión Final puede aprobar en este nivel, en su pantalla solo aparecen las tarjetas de sus proyectos (el sistema lo resuelve por el clerk de Nómina del proyecto y, si no hay, por el Revisor Final del proyecto).' },
        { type: 'steps', items: [
          'En "Horas", abra una tarjeta en "Revisión Final" (se abre en modo revisión).',
          'Revise las horas de cada trabajador (regulares "R" y extra "E") y sus tareas.',
          'Use la barra "AMGtime" para comparar las horas con los ponches reales; pulse "Refrescar" si necesita actualizarlas.',
        ] },
        { type: 'note', variant: 'info', text: 'Indicadores AMGtime: ✓ verde = coincide con el ponche; ámbar = hay diferencia; "Sin ponche" = el trabajador no tiene registro ese día.' },
      ],
    },
    {
      id: 'aprobar-devolver',
      group: 'Nómina',
      title: 'Aprobar o devolver tarjetas (final)',
      blocks: [
        { type: 'steps', items: [
          'Si la tarjeta está correcta, pulse "Aprobar": queda aprobada (es el último nivel) y lista para exportar a nómina.',
          'Si necesita correcciones, pulse "Devolver", indique la razón (obligatoria) y las notas.',
        ] },
        { type: 'note', variant: 'info', text: 'Al devolver, la tarjeta regresa al nivel anterior: al Gerente en proyectos de 3 niveles, o al Ingeniero(a) en proyectos de 2 niveles.' },
        { type: 'note', variant: 'warning', text: 'No puede devolver una tarjeta que usted mismo creó.' },
      ],
    },
    {
      id: 'crear-tarjeta',
      group: 'Nómina',
      title: 'Crear una tarjeta de tiempo',
      blocks: [
        { type: 'paragraph', text: 'Usted también puede crear tarjetas (en "Horas", abra el proyecto, elija la fecha y pulse "Nueva Tarjeta", agregue trabajadores, tareas y horas).' },
        { type: 'note', variant: 'warning', text: 'Como usted es el aprobador final, al someter una tarjeta que usted mismo creó, esta queda aprobada automáticamente. Verifíquela bien antes de someterla.' },
      ],
    },
    {
      id: 'sage-portal',
      group: 'Contabilidad',
      title: 'Exportar a Sage 50 — dónde y cómo',
      blocks: [
        { type: 'paragraph', text: 'Además de aprobar, usted tiene acceso a la exportación a Sage 50. En el menú lateral, abra "Sage Report".' },
        { type: 'paragraph', text: 'El portal tiene dos exportaciones independientes: una para Gastos y otra para Nómina. En ambas, usted primero filtra y previsualiza los registros en pantalla y luego descarga el archivo CSV.' },
        { type: 'note', variant: 'info', text: 'El archivo que se descarga es un CSV con el formato de importación de Sage 50. El equipo de Cuentas por Pagar (AP) lo importa manualmente a Sage (el Código GL corresponde al Account ID y el proyecto al Job ID). Usted solo genera y entrega el archivo; no necesita editar su formato.' },
      ],
    },
    {
      id: 'exportar-gastos',
      group: 'Contabilidad',
      title: 'Exportar Gastos a CSV (Sage)',
      blocks: [
        { type: 'steps', items: [
          'En "Sage Report", use los filtros de Gastos: rango de fechas (desde / hasta), método de pago, tarjeta de crédito (cuando el método es "Tarjeta de crédito") y proyecto.',
          'Revise en pantalla la previsualización de los recibos que se incluirán antes de descargar.',
          'Pulse "Exportar Pagados" para descargar el CSV de Sage.',
        ] },
        { type: 'note', variant: 'warning', text: 'IMPORTANTE — duplicados: por defecto la exportación EXCLUYE los recibos que ya fueron exportados antes. Si un recibo no aparece, lo más probable es que ya se haya exportado. Marque la casilla "Incluir exportados" solo cuando necesite volver a exportarlos.' },
        { type: 'note', variant: 'info', text: 'Si ve el mensaje "No hay items pagados nuevos para exportar", significa que todo lo que coincide con sus filtros ya se exportó. Marque "Incluir exportados" para re-exportarlos.' },
      ],
    },
    {
      id: 'exportar-nomina',
      group: 'Contabilidad',
      title: 'Exportar Nómina a CSV (Sage)',
      blocks: [
        { type: 'steps', items: [
          'En "Sage Report", abra la exportación de Nómina y use los filtros: rango de fechas y proyecto.',
          'Revise la previsualización de las tarjetas aprobadas que se incluirán antes de descargar.',
          'Pulse "Exportar CSV (Sage)" para descargar el archivo.',
        ] },
        { type: 'note', variant: 'warning', text: 'IMPORTANTE — duplicados: igual que en Gastos, por defecto se EXCLUYEN los registros ya exportados. Marque "Incluir exportados" solo si necesita re-exportarlos. El mensaje "No hay registros nuevos para exportar" indica que ya se exportaron.' },
        { type: 'note', variant: 'info', text: 'Las entradas codificadas a Cuentas GL todavía NO se incluyen en el archivo Sage (el mapeo GL→Sage está pendiente de Contabilidad). Si el sistema excluye entradas por esta razón, le mostrará un aviso con la cantidad.' },
      ],
    },
  ],
};
